import PaginatedTable from '../../components/ui/PaginatedTable';
import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import DashboardLayout from "../Layouts/DashboardLayout";

const API = "/api";
const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function GSTReports() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [summary, setSummary] = useState({
    invoice_count: 0,
    pos_count: 0,
    taxable_sales: 0,
    gst_collected: 0,
    cgst: 0,
    sgst: 0,
    total_sales: 0,
  });
  const [reports, setReports] = useState([]);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/gst-reports?month=${month}&year=${year}`,
        { credentials: "include" },
      );
      const data = await response.json();
      if (!response.ok || data.status !== "success")
        throw new Error(data.message || "Failed to load GST report");
      setSummary(data.summary);
      setReports(data.data);
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadReport();
  }, [month, year]);
  const filtered = useMemo(
    () => reports.filter((record) => !source || record.source === source),
    [reports, source],
  );
  const exportExcel = () => {
    const rows = filtered.map((r) => ({
      Source: r.source,
      "Invoice / Bill": r.invoice_no,
      "Order / Bill No": r.order_no,
      Customer: r.customer,
      GSTIN: r.gst_no || "",
      Branch: r.branch || "",
      Date: r.date,
      "Taxable Value": Number(r.subtotal) - Number(r.discount),
      Discount: Number(r.discount),
      "Total GST": Number(r.tax),
      CGST: Number(r.cgst),
      SGST: Number(r.sgst),
      "Grand Total": Number(r.grand_total),
      "Paid Amount": Number(r.paid_amount),
      Balance: Number(r.balance_amount),
      Status: r.status,
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 15 },
      { wch: 18 },
      { wch: 18 },
      { wch: 28 },
      { wch: 20 },
      { wch: 22 },
      { wch: 14 },
      { wch: 16 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
      { wch: 12 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "GST Report");
    XLSX.writeFile(
      workbook,
      `jaggis-gst-${year}-${String(month).padStart(2, "0")}.xlsx`,
    );
  };
  // Kept as an alias so the existing header button invokes the Excel export.
  const exportCsv = exportExcel;
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-serif">GST Reports</h1>
            <p className="mt-1 text-sm text-gray-400">
              Combined GST for Sales Orders and POS bills. Void and refund bills
              are excluded.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm"
            >
              {months.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm"
            >
              {[2024, 2025, 2026, 2027, 2028].map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
            <button
              onClick={exportCsv}
              className="rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-black"
            >
              Export Excel
            </button>
          </div>
        </header>
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card title="Sales-order invoices" value={summary.invoice_count} />
          <Card title="POS bills" value={summary.pos_count} />
          <Card title="Taxable sales" value={money(summary.taxable_sales)} />
          <Card
            title="Total GST"
            value={money(summary.gst_collected)}
            accent="text-amber-300"
          />
          <Card title="CGST" value={money(summary.cgst)} />
          <Card title="SGST" value={money(summary.sgst)} />
          <Card
            title="Total sales"
            value={money(summary.total_sales)}
            accent="text-emerald-300"
          />
        </section>
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex gap-2">
            <button
              onClick={() => setSource("")}
              className={`rounded-lg px-3 py-2 text-sm ${!source ? "bg-amber-400 text-black" : "bg-black/30 text-gray-300"}`}
            >
              All ({reports.length})
            </button>
            <button
              onClick={() => setSource("Sales Order")}
              className={`rounded-lg px-3 py-2 text-sm ${source === "Sales Order" ? "bg-amber-400 text-black" : "bg-black/30 text-gray-300"}`}
            >
              Sales Orders
            </button>
            <button
              onClick={() => setSource("POS")}
              className={`rounded-lg px-3 py-2 text-sm ${source === "POS" ? "bg-amber-400 text-black" : "bg-black/30 text-gray-300"}`}
            >
              POS
            </button>
          </div>
          <span className="text-sm text-gray-400">
            {loading ? "Loading…" : `${filtered.length} records`}
          </span>
        </section>
        <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#0b0b0f]">
          <PaginatedTable className="w-full min-w-[1450px] text-sm">
            <thead className="bg-black/40 text-left text-amber-400">
              <tr>
                {[
                  "Source",
                  "Invoice / Bill",
                  "Customer / GSTIN",
                  "Branch",
                  "Date",
                  "Taxable",
                  "Discount",
                  "GST",
                  "CGST",
                  "SGST",
                  "Total",
                  "Paid",
                  "Balance",
                  "Status",
                ].map((label) => (
                  <th key={label} className="p-3">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="14" className="p-10 text-center text-gray-500">
                    No GST records for this period.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr
                    key={`${r.source}-${r.id}`}
                    className="border-t border-white/10 hover:bg-white/5"
                  >
                    <td className="p-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs ${r.source === "POS" ? "bg-sky-400/15 text-sky-300" : "bg-amber-400/15 text-amber-300"}`}
                      >
                        {r.source}
                      </span>
                    </td>
                    <td className="p-3 font-semibold">
                      {r.invoice_no}
                      <div className="mt-1 text-xs text-gray-500">
                        {r.order_no}
                      </div>
                    </td>
                    <td className="p-3">
                      {r.customer}
                      <div className="mt-1 text-xs text-gray-500">
                        {r.gst_no || "GSTIN not provided"}
                      </div>
                    </td>
                    <td className="p-3">{r.branch || "-"}</td>
                    <td className="p-3">
                      {new Date(r.date).toLocaleDateString("en-IN")}
                    </td>
                    <td className="p-3 text-right">
                      {money(r.subtotal - r.discount)}
                    </td>
                    <td className="p-3 text-right text-red-300">
                      {money(r.discount)}
                    </td>
                    <td className="p-3 text-right text-amber-300">
                      {money(r.tax)}
                    </td>
                    <td className="p-3 text-right">{money(r.cgst)}</td>
                    <td className="p-3 text-right">{money(r.sgst)}</td>
                    <td className="p-3 text-right font-semibold">
                      {money(r.grand_total)}
                    </td>
                    <td className="p-3 text-right text-emerald-300">
                      {money(r.paid_amount)}
                    </td>
                    <td className="p-3 text-right text-red-300">
                      {money(r.balance_amount)}
                    </td>
                    <td className="p-3">
                      <Status value={r.status} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </PaginatedTable>
        </div>
      </div>
    </DashboardLayout>
  );
}
function Card({ title, value, accent = "text-white" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <p className="text-xs uppercase tracking-wide text-gray-400">{title}</p>
      <p className={`mt-2 text-2xl font-semibold ${accent}`}>{value}</p>
    </div>
  );
}
function Status({ value }) {
  const style =
    String(value).toLowerCase() === "paid"
      ? "bg-emerald-400/15 text-emerald-300"
      : String(value).toLowerCase() === "partial"
        ? "bg-sky-400/15 text-sky-300"
        : "bg-amber-400/15 text-amber-300";
  return (
    <span className={`rounded-full px-2 py-1 text-xs ${style}`}>{value}</span>
  );
}
